import { useEffect, useMemo, useRef } from 'react';
import { TDocument, TDraft, TVersion, TTemplateField } from '../types';
import { getVersionId } from '@modules/items';

/**
 * Manages the template dropdown options ref and resolves the version-specific template blob name.
 *
 * Extracted from DocumentContext to keep template-specific logic self-contained.
 */
const useTemplateDropdownOptions = (doc: TDocument, version: TVersion | TDraft | undefined) => {
  const templateDropdownOptionsRef = useRef<Record<string, string[]>>({});

  const activeTemplateSource = version?.templateSource;
  const isBound = !!activeTemplateSource?.fieldValuesBlobName;
  const templateVersionId = activeTemplateSource?.versionId;

  // Resolve the template blob name for the viewed version.
  // Fixes a bug where viewing older bound versions showed the ACTIVE template version's content.
  const viewedTemplateBlobName = useMemo<string | undefined>(() => {
    if (!isBound || !doc.templateBlobInfo || !templateVersionId) return undefined;
    return doc.templateBlobInfo.versionBlobNames?.[templateVersionId];
  }, [isBound, doc.templateBlobInfo, templateVersionId]);

  // Dropdown options ref - initialized from versionTemplateFields (or template's own fields for templates).
  // Deps use a version identity key (version.id or 'draft') rather than the full version object
  // to avoid re-running on every version reference change - only when the viewed version actually changes.
  useEffect(() => {
    let fields: TTemplateField[] | undefined;
    if (doc.isTemplate) {
      fields = version?.templateFields; // Template: own draft/version fields
    } else if (activeTemplateSource && doc.templateBlobInfo && templateVersionId) {
      // Bound or detached: resolve from versionTemplateFields map
      fields = doc.templateBlobInfo.versionTemplateFields?.[templateVersionId];
    }
    const map: Record<string, string[]> = {};
    for (const f of fields || []) {
      if (f.type === 'DROPDOWN' && f.options) map[f.fieldId] = f.options;
    }
    templateDropdownOptionsRef.current = map;
  }, [version ? getVersionId(version) : 'draft', templateVersionId, doc.templateBlobInfo]);

  return { templateDropdownOptionsRef, viewedTemplateBlobName, isBound };
};

export default useTemplateDropdownOptions;
